import React, { forwardRef, useImperativeHandle } from 'react';
import { FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import { useExtraFieldsFormValidation7 } from '../useExtraFieldsFormValidation7';
import { useObjectExtraFields } from '../useObjectExtraFields';
import {
  ExtraFieldsArea7,
  Props as ExtraFieldsAreaProps,
} from './ExtraFieldsArea7';
import { Loading7 } from './ExtraFieldsAreaContent7';
import { useExtraFieldsFormSimple7 } from './useExtraFieldsFormSimple7';

type Props = ExtraFieldsAreaProps & {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null;
};

export type ExtraFieldsAreaSimple7Ref = {
  triggerValidation: () => Promise<boolean>;
};

export const ExtraFieldsAreaSimple7 = forwardRef<
  ExtraFieldsAreaSimple7Ref,
  Props
>(({ onChange, values, recordTypeName, ...restProps }, ref) => {
  const { rawObjectExtraFields, getFormFields, loading } = useObjectExtraFields(
    restProps.objectApiName,
  );
  const formFields = getFormFields(restProps.formId);
  const { applyExtraFieldsValidationSchema } = useExtraFieldsFormValidation7({
    extraFieldFormInfos: formFields,
    contextParams: restProps.contextParams ?? [],
  });
  const validationSchema = applyExtraFieldsValidationSchema(yup.object());

  const { formMethods } = useExtraFieldsFormSimple7({
    values,
    recordTypeName,
    availableRecordTypes: rawObjectExtraFields?.availableRecordTypes ?? [],
    onChange,
    validationSchema,
    inputsMounted: !loading && !!rawObjectExtraFields && formFields.length > 0,
  });

  const { trigger } = formMethods;
  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: () =>
        loading || !rawObjectExtraFields ? Promise.resolve(true) : trigger(),
    }),
    [loading, rawObjectExtraFields, trigger],
  );

  if (loading) {
    return <Loading7 />;
  }
  if (!rawObjectExtraFields) {
    return null;
  }

  return (
    <FormProvider {...formMethods}>
      <ExtraFieldsArea7 {...restProps} />
    </FormProvider>
  );
});

ExtraFieldsAreaSimple7.displayName = 'ExtraFieldsAreaSimple7';
